import { useState, useEffect, useCallback } from 'react';
import logger from '@/utils/logger';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { marked } from 'marked';
import { ArrowLeft, Save, Loader2, Eye, AlertCircle, Calendar, Tag, EyeOff, Sparkles, Clock, Search, History, RotateCcw } from 'lucide-react';
import RichTextEditor from '@/components/RichTextEditor';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import Navbar from '@/components/Navbar';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORIES = [
  { value: 'general', label: 'General' },
  { value: 'tutorials', label: 'Tutorials' },
  { value: 'tips', label: 'Tips & Tricks' },
  { value: 'patterns', label: 'Patterns' },
  { value: 'news', label: 'News' },
  { value: 'reviews', label: 'Reviews' },
  { value: 'inspiration', label: 'Inspiration' },
];

const LANGUAGES = [
  { value: 'en', label: 'English', flag: '🇬🇧' },
  { value: 'fi', label: 'Suomi (Finnish)', flag: '🇫🇮' },
];

export default function AdminBlogForm() {
  const { postId } = useParams();
  const navigate = useNavigate();
  const isEditing = !!postId;

  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [isAdmin, setIsAdmin] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [revisions, setRevisions] = useState([]);
  const [showRevisions, setShowRevisions] = useState(false);
  const [restoringRevision, setRestoringRevision] = useState(null);
  
  const [formData, setFormData] = useState({
    title: '',
    excerpt: '',
    content: '',
    featured_image: '',
    gallery_images: '',
    category: 'general',
    tags: '',
    is_published: false,
    is_featured: false,
    is_pinned: false,
    scheduled_at: '',
    meta_title: '',
    meta_description: '',
    language: 'en',  // Default to English
  });

  const checkAdmin = useCallback(async () => {
    try {
      await axios.get(`${API}/admin/blog`, { withCredentials: true });
      setIsAdmin(true);
    } catch (error) {
      if (error.response?.status === 403) {
        setIsAdmin(false);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const loadRevisions = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/blog/${postId}/revisions`, { withCredentials: true });
      setRevisions(response.data || []);
    } catch (error) {
      logger.error('Failed to load revisions:', error);
    }
  }, [postId]);

  const loadPost = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/blog`, { withCredentials: true });
      const post = response.data.find(p => p.post_id === postId);
      
      if (post) {
        setFormData({
          title: post.title || '',
          excerpt: post.excerpt || '',
          content: post.content || '',
          featured_image: post.featured_image || '',
          gallery_images: (post.gallery_images || []).join('\n'),
          category: post.category || 'general',
          tags: (post.tags || []).join(', '),
          is_published: post.is_published || false,
          is_featured: post.is_featured || false,
          is_pinned: post.is_pinned || false,
          scheduled_at: post.scheduled_at ? new Date(post.scheduled_at).toISOString().slice(0, 16) : '',
          meta_title: post.meta_title || '',
          meta_description: post.meta_description || '',
          language: post.language || 'en',
        });
        setIsAdmin(true);
        loadRevisions();
      } else {
        toast.error('Post not found');
        navigate('/admin/blog');
      }
    } catch (error) {
      if (error.response?.status === 403) {
        setIsAdmin(false);
      } else {
        logger.error('Failed to load post:', error);
        toast.error('Failed to load post');
      }
    } finally {
      setLoading(false);
    }
  }, [postId, navigate, loadRevisions]);

  useEffect(() => {
    if (isEditing) {
      loadPost();
    } else {
      checkAdmin();
    }
  }, [isEditing, loadPost, checkAdmin]);

  const handleRestoreRevision = async (revisionId) => {
    if (!window.confirm('Are you sure you want to restore this revision? Current changes will be saved as a new revision.')) {
      return;
    }
    
    setRestoringRevision(revisionId);
    try {
      await axios.post(`${API}/admin/blog/${postId}/revisions/${revisionId}/restore`, {}, { withCredentials: true });
      toast.success('Post restored to revision');
      loadPost();
      setShowRevisions(false);
    } catch (error) {
      logger.error('Failed to restore revision:', error);
      toast.error('Failed to restore revision');
    } finally {
      setRestoringRevision(null);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // DOMPurify config - preserve all structural HTML tags for formatting
  const PURIFY_CONFIG = {
    ALLOWED_TAGS: ['p', 'br', 'strong', 'b', 'em', 'i', 'u', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li', 'a', 'img', 'blockquote', 'span', 'div', 'pre', 'code', 'table', 'thead', 'tbody', 'tr', 'th', 'td', 'hr'],
    ALLOWED_ATTR: ['href', 'src', 'alt', 'target', 'rel', 'class', 'style'],
    KEEP_CONTENT: true
  };

  // Render content - supports both HTML (from editor) and Markdown (legacy)
  const renderContent = (content) => {
    if (!content) return '';
    // If content contains HTML tags, it's from the editor - sanitize with config to preserve formatting
    if (content.includes('<') && content.includes('>')) {
      return DOMPurify.sanitize(content, PURIFY_CONFIG);
    }
    // Otherwise treat as Markdown (legacy content)
    const rawHtml = marked(content);
    return DOMPurify.sanitize(rawHtml, PURIFY_CONFIG);
  };

  // Keep old function name for compatibility
  const renderMarkdown = renderContent;

  const getTagsArray = () => {
    if (!formData.tags) return [];
    return formData.tags.split(',').map(t => t.trim()).filter(Boolean);
  };

  const formatDate = () => {
    return new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      toast.error('Please enter a title');
      return;
    }
    
    if (!formData.excerpt.trim()) {
      toast.error('Please enter an excerpt');
      return;
    }

    if (!formData.content.trim()) {
      toast.error('Please enter content');
      return;
    }

    setSaving(true);

    try {
      const payload = {
        title: formData.title.trim(),
        excerpt: formData.excerpt.trim(),
        content: formData.content,
        featured_image: formData.featured_image.trim() || null,
        gallery_images: formData.gallery_images.split('\n').map(u => u.trim()).filter(Boolean),
        category: formData.category,
        tags: formData.tags.split(',').map(t => t.trim()).filter(Boolean),
        is_published: formData.is_published,
        is_featured: formData.is_featured,
        is_pinned: formData.is_pinned,
        scheduled_at: formData.scheduled_at ? new Date(formData.scheduled_at).toISOString() : null,
        meta_title: formData.meta_title.trim() || null,
        meta_description: formData.meta_description.trim() || null,
        language: formData.language,
      };

      if (isEditing) {
        await axios.put(`${API}/admin/blog/${postId}`, payload, { withCredentials: true });
        toast.success('Post updated successfully');
      } else {
        await axios.post(`${API}/admin/blog`, payload, { withCredentials: true });
        toast.success(payload.scheduled_at ? 'Post scheduled successfully' : 'Post created successfully');
      }

      navigate('/admin/blog');
    } catch (error) {
      logger.error('Failed to save post:', error);
      toast.error(error.response?.data?.detail || 'Failed to save post');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <Loader2 className="h-12 w-12 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  if (isAdmin === false) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <div className="text-center">
            <AlertCircle className="h-16 w-16 text-destructive mx-auto mb-4" />
            <h1 className="text-2xl font-heading font-bold mb-2">Access Denied</h1>
            <p className="text-muted-foreground mb-6">
              You don&apos;t have permission to access the admin area.
            </p>
            <Button onClick={() => navigate('/dashboard')} className="rounded-full">
              Go to Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Button
          variant="ghost"
          onClick={() => navigate('/admin/blog')}
          className="mb-6 -ml-2 rounded-full"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Blog
        </Button>

        <div className="flex justify-between items-start mb-8">
          <div>
            <h1 className="text-4xl font-heading font-extrabold tracking-tight mb-2">
              {isEditing ? 'Edit Post' : 'Create New Post'}
            </h1>
            <p className="text-muted-foreground">
              {isEditing ? 'Update your blog post' : 'Write a new blog post'}
            </p>
          </div>
          <Button
            variant="outline"
            onClick={() => setShowPreview(!showPreview)}
            className="rounded-full"
          >
            <Eye className="h-4 w-4 mr-2" />
            {showPreview ? 'Edit' : 'Preview'}
          </Button>
        </div>

        {showPreview ? (
          <div className="space-y-6">
            {/* Draft Banner */}
            <div className="rounded-2xl bg-amber-50 border border-amber-200 p-4 flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                <EyeOff className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="font-medium text-amber-800">Draft Preview</p>
                <p className="text-sm text-amber-600">
                  This is how your post will look when published. Only you can see this preview.
                </p>
              </div>
            </div>

            {/* Preview Content - Matches BlogPost.js layout */}
            <article className="rounded-3xl bg-white border border-stone-100 overflow-hidden">
              {/* Featured Image */}
              {formData.featured_image && (
                <div className="aspect-video overflow-hidden">
                  <img
                    src={formData.featured_image}
                    alt={formData.title}
                    className="w-full h-full object-cover"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                </div>
              )}

              <div className="p-8 md:p-12">
                {/* Post Header */}
                <header className="mb-8">
                  <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground mb-4">
                    <span className="flex items-center">
                      <Calendar className="h-4 w-4 mr-1" />
                      {formatDate()}
                    </span>
                    <span className="px-3 py-1 rounded-full bg-primary/10 text-primary font-medium capitalize">
                      {formData.category}
                    </span>
                    {formData.is_featured && (
                      <span className="flex items-center px-3 py-1 rounded-full bg-amber-100 text-amber-700 font-medium">
                        <Sparkles className="h-3 w-3 mr-1" />
                        Featured
                      </span>
                    )}
                    {!formData.is_published && formData.scheduled_at && (
                      <span className="flex items-center px-3 py-1 rounded-full bg-blue-100 text-blue-700 font-medium">
                        <Clock className="h-3 w-3 mr-1" />
                        Scheduled
                      </span>
                    )}
                    {!formData.is_published && !formData.scheduled_at && (
                      <span className="px-3 py-1 rounded-full bg-stone-100 text-stone-600 font-medium">
                        Draft
                      </span>
                    )}
                  </div>

                  <h1 className="text-3xl md:text-4xl font-heading font-bold mb-4">
                    {formData.title || 'Untitled Post'}
                  </h1>

                  <p className="text-lg text-muted-foreground">
                    {formData.excerpt || 'No excerpt provided'}
                  </p>

                  {/* Tags */}
                  {getTagsArray().length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-6">
                      {getTagsArray().map(tag => (
                        <span
                          key={tag}
                          className="flex items-center px-3 py-1 rounded-full bg-stone-100 text-stone-600 text-sm"
                        >
                          <Tag className="h-3 w-3 mr-1" />
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </header>

                {/* Post Content with Markdown */}
                <div 
                  className="prose prose-stone prose-lg max-w-none
                    prose-headings:font-heading prose-headings:font-bold
                    prose-h1:text-3xl prose-h2:text-2xl prose-h3:text-xl
                    prose-p:text-stone-600 prose-p:leading-relaxed
                    prose-a:text-primary prose-a:no-underline hover:prose-a:underline
                    prose-strong:text-stone-800
                    prose-ul:list-disc prose-ol:list-decimal
                    prose-li:text-stone-600
                    prose-blockquote:border-l-primary prose-blockquote:bg-stone-50 prose-blockquote:py-1 prose-blockquote:px-4 prose-blockquote:rounded-r-lg
                    prose-code:bg-stone-100 prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:text-sm
                    prose-pre:bg-stone-900 prose-pre:text-stone-100"
                  dangerouslySetInnerHTML={{ __html: renderMarkdown(formData.content) }}
                />

                {!formData.content && (
                  <p className="text-muted-foreground italic">No content yet. Start writing in the editor!</p>
                )}

                {/* Gallery Preview */}
                {formData.gallery_images && formData.gallery_images.split('\n').filter(u => u.trim()).length > 0 && (
                  <div className="mt-10 pt-8 border-t border-stone-100">
                    <h3 className="flex items-center text-lg font-heading font-semibold mb-4">
                      <Search className="h-5 w-5 mr-2 text-primary" />
                      Gallery Preview ({formData.gallery_images.split('\n').filter(u => u.trim()).length} images)
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                      {formData.gallery_images.split('\n').filter(u => u.trim()).map((url, idx) => (
                        <div
                          key={url.trim() || idx}
                          className="aspect-square rounded-xl overflow-hidden bg-stone-100"
                        >
                          <img
                            src={url.trim()}
                            alt={`Gallery ${idx + 1}`}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              const fallback = document.createElement('div');
                              fallback.className = 'w-full h-full flex items-center justify-center text-stone-400 text-xs';
                              fallback.textContent = 'Image failed';
                              e.target.parentElement.appendChild(fallback);
                            }}
                          />
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-muted-foreground mt-3">
                      This is how the gallery will appear below your post content. Readers can click images to view them in a full-screen lightbox.
                    </p>
                  </div>
                )}
              </div>
            </article>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Basic Info */}
            <div className="rounded-3xl bg-white border border-stone-100 p-8">
              <h2 className="text-xl font-heading font-semibold mb-6">Post Details</h2>
              
              <div className="grid gap-6">
                <div>
                  <Label htmlFor="title">Title *</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => handleChange('title', e.target.value)}
                    placeholder="Enter post title"
                    className="mt-1 rounded-xl"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="excerpt">Excerpt *</Label>
                  <Textarea
                    id="excerpt"
                    value={formData.excerpt}
                    onChange={(e) => handleChange('excerpt', e.target.value)}
                    placeholder="Brief summary of the post (shown in listings)"
                    className="mt-1 rounded-xl min-h-[80px]"
                    required
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <Label>Language</Label>
                    <Select value={formData.language} onValueChange={(v) => handleChange('language', v)}>
                      <SelectTrigger className="mt-1 rounded-xl">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {LANGUAGES.map(lang => (
                          <SelectItem key={lang.value} value={lang.value}>
                            <span className="flex items-center gap-2">
                              <span>{lang.flag}</span>
                              <span>{lang.label}</span>
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground mt-1">
                      Post will appear for users with this language
                    </p>
                  </div>
                  
                  <div>
                    <Label>Category</Label>
                    <Select value={formData.category} onValueChange={(v) => handleChange('category', v)}>
                      <SelectTrigger className="mt-1 rounded-xl">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CATEGORIES.map(cat => (
                          <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="tags">Tags (comma-separated)</Label>
                    <Input
                      id="tags"
                      value={formData.tags}
                      onChange={(e) => handleChange('tags', e.target.value)}
                      placeholder="e.g., crochet, beginners"
                      className="mt-1 rounded-xl"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="featured_image">Featured Image URL</Label>
                  <Input
                    id="featured_image"
                    value={formData.featured_image}
                    onChange={(e) => handleChange('featured_image', e.target.value)}
                    placeholder="https://..."
                    className="mt-1 rounded-xl"
                  />
                  {formData.featured_image && (
                    <div className="mt-2 rounded-xl overflow-hidden h-40">
                      <img
                        src={formData.featured_image}
                        alt="Preview"
                        className="w-full h-full object-cover"
                        onError={(e) => e.target.style.display = 'none'}
                      />
                    </div>
                  )}
                </div>

                <div>
                  <Label htmlFor="gallery_images">Image Gallery URLs (one per line)</Label>
                  <Textarea
                    id="gallery_images"
                    value={formData.gallery_images}
                    onChange={(e) => handleChange('gallery_images', e.target.value)}
                    placeholder="https://example.com/image1.jpg
https://example.com/image2.jpg"
                    className="mt-1 rounded-xl min-h-[100px]"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Add multiple image URLs to create a gallery. Images will display below the post content.
                  </p>
                  {formData.gallery_images && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {formData.gallery_images.split('\n').filter(u => u.trim()).slice(0, 6).map((url, idx) => (
                        <div key={url.trim() || idx} className="w-16 h-16 rounded-lg overflow-hidden bg-stone-100">
                          <img
                            src={url.trim()}
                            alt={`Gallery ${idx + 1}`}
                            className="w-full h-full object-cover"
                            onError={(e) => e.target.style.display = 'none'}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="rounded-3xl bg-white border border-stone-100 p-8">
              <h2 className="text-xl font-heading font-semibold mb-6">Content</h2>
              
              <div>
                <Label htmlFor="content">Post Content *</Label>
                <p className="text-xs text-muted-foreground mb-3">
                  Käytä työkalupalkkia muotoiluun. Voit myös liittää tekstiä suoraan Wordista tai Notionista.
                </p>
                
                <RichTextEditor
                  value={formData.content}
                  onChange={(html) => handleChange('content', html)}
                  placeholder="Kirjoita blogipostauksen sisältö tähän..."
                />
              </div>
            </div>

            {/* Publishing */}
            <div className="rounded-3xl bg-white border border-stone-100 p-8">
              <h2 className="text-xl font-heading font-semibold mb-6">Publishing</h2>
              
              <div className="grid gap-6">
                <div className="flex items-center justify-between p-4 bg-stone-50 rounded-xl">
                  <div>
                    <p className="font-medium">Published</p>
                    <p className="text-sm text-muted-foreground">Make visible on the blog</p>
                  </div>
                  <Switch
                    checked={formData.is_published}
                    onCheckedChange={(v) => {
                      handleChange('is_published', v);
                      // Clear scheduled date if publishing now
                      if (v) handleChange('scheduled_at', '');
                    }}
                  />
                </div>

                {/* Scheduled Publishing */}
                {!formData.is_published && (
                  <div className="p-4 bg-amber-50 border border-amber-100 rounded-xl">
                    <Label htmlFor="scheduled_at" className="font-medium text-amber-800">
                      Schedule Publishing
                    </Label>
                    <p className="text-sm text-amber-600 mb-3">
                      Set a future date/time to auto-publish this post
                    </p>
                    <Input
                      id="scheduled_at"
                      type="datetime-local"
                      value={formData.scheduled_at}
                      onChange={(e) => handleChange('scheduled_at', e.target.value)}
                      min={new Date().toISOString().slice(0, 16)}
                      className="rounded-xl bg-white"
                    />
                    {formData.scheduled_at && (
                      <p className="text-xs text-amber-700 mt-2">
                        Will publish on: {new Date(formData.scheduled_at).toLocaleString()}
                      </p>
                    )}
                  </div>
                )}

                <div className="flex items-center justify-between p-4 bg-stone-50 rounded-xl">
                  <div>
                    <p className="font-medium">Featured</p>
                    <p className="text-sm text-muted-foreground">Show on homepage</p>
                  </div>
                  <Switch
                    checked={formData.is_featured}
                    onCheckedChange={(v) => handleChange('is_featured', v)}
                  />
                </div>

                <div className="flex items-center justify-between p-4 bg-amber-50 rounded-xl">
                  <div>
                    <p className="font-medium">Pinned</p>
                    <p className="text-sm text-muted-foreground">Show at top of blog list</p>
                  </div>
                  <Switch
                    checked={formData.is_pinned}
                    onCheckedChange={(v) => handleChange('is_pinned', v)}
                  />
                </div>
              </div>
            </div>

            {/* SEO Settings */}
            <div className="rounded-3xl bg-white border border-stone-100 p-8">
              <h2 className="text-xl font-heading font-semibold mb-6 flex items-center">
                <Search className="h-5 w-5 mr-2 text-primary" />
                SEO Settings
              </h2>
              
              <div className="grid gap-6">
                <div>
                  <Label htmlFor="meta_title">Meta Title</Label>
                  <Input
                    id="meta_title"
                    value={formData.meta_title}
                    onChange={(e) => handleChange('meta_title', e.target.value)}
                    placeholder="Custom title for search engines (optional)"
                    className="rounded-xl mt-2"
                    maxLength={70}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    {formData.meta_title.length}/70 characters. Leave empty to use post title.
                  </p>
                </div>

                <div>
                  <Label htmlFor="meta_description">Meta Description</Label>
                  <Textarea
                    id="meta_description"
                    value={formData.meta_description}
                    onChange={(e) => handleChange('meta_description', e.target.value)}
                    placeholder="Brief description for search results (optional)"
                    className="rounded-xl mt-2 min-h-[80px]"
                    maxLength={160}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    {formData.meta_description.length}/160 characters. Leave empty to use excerpt.
                  </p>
                </div>
              </div>
            </div>

            {/* Revision History (only for existing posts) */}
            {isEditing && (
              <div className="rounded-3xl bg-white border border-stone-100 p-8">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-heading font-semibold flex items-center">
                    <History className="h-5 w-5 mr-2 text-primary" />
                    Revision History
                  </h2>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowRevisions(!showRevisions)}
                    className="rounded-full"
                  >
                    {showRevisions ? 'Hide' : `Show (${revisions.length})`}
                  </Button>
                </div>
                
                {showRevisions && (
                  <div className="space-y-3">
                    {revisions.length === 0 ? (
                      <p className="text-muted-foreground text-sm">No revisions yet. Revisions are saved automatically when you update the post.</p>
                    ) : (
                      revisions.map((rev) => (
                        <div
                          key={rev.revision_id}
                          className="flex items-center justify-between p-4 bg-stone-50 rounded-xl"
                        >
                          <div>
                            <p className="font-medium text-sm">{rev.title}</p>
                            <p className="text-xs text-muted-foreground">
                              {new Date(rev.created_at).toLocaleString()}
                            </p>
                          </div>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => handleRestoreRevision(rev.revision_id)}
                            disabled={restoringRevision === rev.revision_id}
                            className="rounded-full"
                          >
                            {restoringRevision === rev.revision_id ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              <>
                                <RotateCcw className="h-3 w-3 mr-1" />
                                Restore
                              </>
                            )}
                          </Button>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Submit */}
            <div className="flex justify-end gap-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/admin/blog')}
                className="rounded-full"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={saving}
                className="rounded-full bg-primary text-primary-foreground shadow-lg"
              >
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    {isEditing ? 'Update Post' : 'Create Post'}
                  </>
                )}
              </Button>
            </div>
          </form>
        )}
      </main>
    </div>
  );
}
